import type { ComponentType, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import type { DocEntry } from './types';

/*
 * A titled panel of doc rows. The landing page's Company/Team recommended and
 * Starred lists all use it; each supplies its own leading icon, empty line,
 * and optional `limit`/`moreTo` for a longer list that defers past a cutoff.
 */
const useStyles = makeStyles({
  panel: {
    borderRadius: 12,
    background: 'var(--bui-white)',
    boxShadow: 'var(--hone-portal-elevation-card)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 20px',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 42,
    padding: '0 20px',
    borderBottom: '1px solid var(--hone-portal-hairline-row)',
    textDecoration: 'none',
    '&:hover': {
      background: 'var(--bui-bg-neutral-2)',
    },
    // Rows are full-bleed inside a clipping panel, so the ring has to be inset
    // or its sides and corners are cut off.
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'inset 0 0 0 2px #039cfb',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  icon: {
    width: 14,
    height: 14,
    opacity: 0.5,
  },
  title: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--bui-fg-primary)',
  },
  meta: {
    flex: 'none',
    fontSize: 11.5,
    color: 'var(--hone-portal-fg-tertiary)',
  },
  empty: {
    margin: 0,
    padding: '18px 20px',
    fontSize: 12.5,
    color: 'var(--hone-portal-fg-tertiary)',
  },
  more: {
    display: 'block',
    padding: '12px 20px',
    fontSize: 12.5,
    fontWeight: 500,
    // Row links read ink in this design; accent blue here would be the only
    // saturated colour on the page.
    color: 'var(--bui-fg-secondary)',
    textDecoration: 'none',
    '&:hover': {
      background: 'var(--bui-bg-neutral-2)',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'inset 0 0 0 2px #039cfb',
    },
  },
});

export type DocListPanelProps = {
  title: string;
  icon: ComponentType<{ className?: string }>;
  docs: DocEntry[];
  emptyLabel: string;
  meta?: (doc: DocEntry) => ReactNode;
  /** Rows shown before the panel defers the rest to `moreTo`. */
  limit?: number;
  moreTo?: string;
};

export const DocListPanel = ({
  title,
  icon: Icon,
  docs,
  emptyLabel,
  meta,
  limit,
  moreTo,
}: DocListPanelProps) => {
  const classes = useStyles();
  const shown = limit ? docs.slice(0, limit) : docs;
  const hidden = docs.length - shown.length;

  return (
    <section className={classes.panel}>
      <header className={classes.header}>{title}</header>
      {shown.length === 0 ? (
        <p className={classes.empty}>{emptyLabel}</p>
      ) : (
        shown.map(doc => (
          <RouterLink key={doc.entityRef} to={doc.path} className={classes.row}>
            <Icon className={classes.icon} />
            <span className={classes.title}>{doc.title}</span>
            {meta && <span className={classes.meta}>{meta(doc)}</span>}
          </RouterLink>
        ))
      )}
      {hidden > 0 && moreTo && (
        <RouterLink to={moreTo} className={classes.more}>
          {hidden} more…
        </RouterLink>
      )}
    </section>
  );
};
